// src/firebase/analytics.ts
/**
 * Firebase Analytics Configuration and Helper Functions
 * Tracks page views, user interactions, and custom events
 * 
 * Firebase v8 Syntax (Compat Mode)
 */

import firebase from 'firebase/app';
import 'firebase/analytics';

// Get analytics instance (will be initialized after firebase app is ready)
const getAnalytics = () => {
  if (!firebase.apps.length) {
    console.warn('Firebase app not initialized yet');
    return null;
  }
  return firebase.analytics();
};

/**
 * Log page view events
 */
export const logPageView = (pageName: string, pageTitle?: string) => {
  const analyticsInstance = getAnalytics();
  if (!analyticsInstance) return;
  
  analyticsInstance.logEvent('page_view', {
    page_name: pageName,
    page_title: pageTitle || pageName,
    page_location: window.location.href,
    page_path: window.location.pathname,
  });
};

/**
 * Log user login event
 */
export const logLogin = (method: string = 'email') => {
  const analyticsInstance = getAnalytics();
  if (!analyticsInstance) return;
  
  analyticsInstance.logEvent('login', {
    method: method,
  });
};

/**
 * Log user signup event
 */
export const logSignUp = (method: string = 'email') => {
  const analyticsInstance = getAnalytics();
  if (!analyticsInstance) return;
  
  analyticsInstance.logEvent('sign_up', {
    method: method,
  });
};

/**
 * Set user ID for analytics
 */
export const setAnalyticsUserId = (userId: string) => {
  const analyticsInstance = getAnalytics();
  if (!analyticsInstance) return;
  
  analyticsInstance.setUserId(userId);
};

/**
 * Set user properties
 */
export const setAnalyticsUserProperties = (properties: {
  user_role?: 'admin' | 'doctor' | 'patient';
  has_devices?: boolean;
  device_count?: number;
  doctor_name?: string;
}) => {
  const analyticsInstance = getAnalytics();
  if (!analyticsInstance) return;
  
  analyticsInstance.setUserProperties(properties);
};

/**
 * Log reading submission
 */
export const logReadingSubmission = (deviceType: string) => {
  const analyticsInstance = getAnalytics();
  if (!analyticsInstance) return;
  
  analyticsInstance.logEvent('reading_submitted', {
    device_type: deviceType,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Log button click
 */
export const logButtonClick = (buttonName: string, location: string) => {
  const analyticsInstance = getAnalytics();
  if (!analyticsInstance) return;
  
  analyticsInstance.logEvent('button_click', {
    button_name: buttonName,
    location: location,
  });
};

/**
 * Log navigation events
 */
export const logNavigation = (from: string, to: string) => {
  const analyticsInstance = getAnalytics();
  if (!analyticsInstance) return;
  
  analyticsInstance.logEvent('navigation', {
    from_page: from,
    to_page: to,
  });
};

/**
 * Log form submission
 */
export const logFormSubmission = (formName: string, success: boolean) => {
  const analyticsInstance = getAnalytics();
  if (!analyticsInstance) return;
  
  analyticsInstance.logEvent('form_submission', {
    form_name: formName,
    success: success,
  });
};

/**
 * Log profile update
 */
export const logProfileUpdate = (fieldsUpdated: string[]) => {
  const analyticsInstance = getAnalytics();
  if (!analyticsInstance) return;
  
  analyticsInstance.logEvent('profile_update', {
    fields: fieldsUpdated.join(','),
  });
};

/**
 * Log export action
 */
export const logExport = (exportType: string, recordCount: number) => {
  const analyticsInstance = getAnalytics();
  if (!analyticsInstance) return;
  
  analyticsInstance.logEvent('export_data', {
    export_type: exportType,
    record_count: recordCount,
  });
};

/**
 * Log report view
 */
export const logReportView = (reportType: string, patientId?: string) => {
  const analyticsInstance = getAnalytics();
  if (!analyticsInstance) return;
  
  analyticsInstance.logEvent('report_view', {
    report_type: reportType,
    patient_id: patientId,
  });
};

/**
 * Log search action
 */
export const logSearch = (searchTerm: string, resultsCount: number) => {
  const analyticsInstance = getAnalytics();
  if (!analyticsInstance) return;
  
  analyticsInstance.logEvent('search', {
    search_term: searchTerm,
    results_count: resultsCount,
  });
};

/**
 * Log error events
 */
export const logError = (errorMessage: string, errorCode?: string, context?: string) => {
  const analyticsInstance = getAnalytics();
  if (!analyticsInstance) return;
  
  analyticsInstance.logEvent('error', {
    error_message: errorMessage,
    error_code: errorCode,
    context: context,
  });
};

/**
 * Log timing events (for performance tracking)
 */
export const logTiming = (name: string, value: number, category?: string) => {
  const analyticsInstance = getAnalytics();
  if (!analyticsInstance) return;
  
  analyticsInstance.logEvent('timing_complete', {
    name: name,
    value: value,
    category: category,
  });
};

/**
 * Log device pairing attempt
 */
export const logDevicePairing = (deviceType: string, success: boolean) => {
  const analyticsInstance = getAnalytics();
  if (!analyticsInstance) return;
  
  analyticsInstance.logEvent('device_pairing', {
    device_type: deviceType,
    success: success,
  });
};

export default getAnalytics;